import { useState, type CSSProperties } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Menu, Search, ScanLine, Building2, User, Ticket } from './icons';
import { useAuth } from '../contexts/AuthContext';
import { useAuthModal } from '../contexts/AuthModalContext';
import { MobileMenu, MobileMenuLink, MenuTile } from './MobileMenu';
import { HeaderSearch } from './HeaderSearch';
import { ThemeToggleSwitch, ThemeToggleSegment } from './ThemeToggle';
import { UserMenu } from './UserMenu';
import { immersiveConfig } from '../lib/immersive';
import logoUrl from '../assets/logo.webp';

export function Header() {
  const { user, isAuthenticated, logout } = useAuth();
  const { open: openAuthModal } = useAuthModal();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const onSearchPage = location.pathname === '/search';
  // 沉浸式顶栏（移动端：透明浮于 hero 之上 → 下滑变毛玻璃胶囊 + 反白 logo/图标）。
  // 路由配置集中在 lib/immersive.ts（Header 与状态栏配色共用）。range 写入 --zx-hd-range。
  const immersiveCfg = immersiveConfig(location.pathname);
  const immersive = !!immersiveCfg;
  const immersiveRange = immersiveCfg?.range ?? null;

  // 移动端首页顶端沉浸态（logo 反白、图标转白、实体层淡出、渐变遮罩淡出）现由「滚动驱动 CSS」接管
  // （globals.css 的 .zx-home .zx-hd-* + Hero 的 .zx-hero-*）——零 JS scroll 监听、与原生滚动同步、无抖动。
  // 图标按钮带 zx-hd-icon 类参与颜色过渡；首页给 <header> 加 .zx-home 启用这些动画。
  const mobileIconBtnClass =
    'zx-hd-icon flex h-9 w-9 items-center justify-center rounded-full text-fg-muted transition-colors hover:bg-surface-muted hover:text-fg';

  async function handleLogout() {
    setMenuOpen(false);
    await logout();
    navigate('/');
  }

  function closeMenu() {
    setMenuOpen(false);
  }

  return (
    <header
      className={`sticky top-0 z-30${immersive ? ' zx-home' : ''}`}
      style={immersiveRange ? ({ '--zx-hd-range': immersiveRange } as CSSProperties) : undefined}
    >
      {/* 沉浸式渐变遮罩：移动端沉浸页(首页/数据来源)顶端，上半透明→下完全透明，随下滑由滚动驱动动画淡出(.zx-hd-scrim)。
          叠在 hero 之上(hero 用负边距塞到 header 之下)，让首屏 header 融入大图；降级时 opacity:0 隐藏。 */}
      {immersive && (
        <div
          aria-hidden
          className="zx-hd-scrim pointer-events-none absolute inset-0 lg:hidden"
          style={{
            background:
              'linear-gradient(to bottom, rgba(4,12,32,0.52) 0%, rgba(4,12,32,0.18) 52%, rgba(4,12,32,0) 100%)',
          }}
        />
      )}
      {/* ============ 桌面端：三颗悬浮玻璃胶囊（>= lg） ============ */}
      <div className="relative mx-auto hidden w-full max-w-[1600px] items-center gap-3 px-4 pb-2 pt-3 lg:flex">
        {/* 胶囊①：logo + 导航 */}
        <div className="liquid-glass liquid-glass-float flex h-12 shrink-0 items-center gap-0.5 rounded-full border border-line pl-3 pr-2">
          <Link to="/" className="flex items-center pr-1.5">
            <img src={logoUrl} alt="知行数据分析" className="header-logo h-8 w-auto" />
          </Link>
          <NavLink to="/" end className={({ isActive }) => navBlockClass(isActive)}>
            首页
          </NavLink>
          <AllDatasetsNavLink />
          <CategoryNavLink to="/datasets?cat=company&lv=category" catCode="company">
            上市公司数据
          </CategoryNavLink>
          <CategoryNavLink to="/datasets?cat=province&lv=category" catCode="province">
            省级数据
          </CategoryNavLink>
          <CategoryNavLink to="/datasets?cat=city&lv=category" catCode="city">
            地级市数据
          </CategoryNavLink>
          <NavLink to="/redeem" className={({ isActive }) => navBlockClass(isActive)}>
            兑换码
          </NavLink>
        </div>

        {/* 弹性空白：把搜索 + 账户推到最右 */}
        <div className="flex-1" />

        {/* 胶囊②：搜索 */}
        {!onSearchPage && (
          <div className="liquid-glass liquid-glass-float flex h-12 w-[300px] min-w-[160px] items-stretch rounded-full border border-line">
            <HeaderSearch variant="desktop" />
          </div>
        )}

        {/* 胶囊③：日夜切换器 + 账户 */}
        <div className="liquid-glass liquid-glass-float flex h-12 shrink-0 items-center gap-2 rounded-full border border-line px-2.5">
          <ThemeToggleSwitch />
          {isAuthenticated && user ? (
            <UserMenu user={user} onLogout={handleLogout} />
          ) : (
            <>
              <button
                type="button"
                onClick={() => openAuthModal({ mode: 'login' })}
                className="px-1.5 text-[13px] text-fg-muted hover:text-fg"
              >
                登录
              </button>
              <button
                type="button"
                onClick={() => openAuthModal({ mode: 'register' })}
                className="rounded-full bg-navy-fill px-4 py-1.5 text-[13px] text-white hover:bg-navy-fill-strong"
              >
                注册
              </button>
            </>
          )}
        </div>
      </div>

      {/* ============ 移动端（< lg）：单颗悬浮胶囊 ============ */}
      {/* pt 含 env(safe-area-inset-top)：viewport-fit=cover 后把胶囊推到状态栏/刘海之下（非安全区设备 env()=0） */}
      <div className="relative px-3 pt-[calc(0.75rem+env(safe-area-inset-top))] lg:hidden">
        <div className="relative flex h-12 items-center justify-between gap-2 rounded-full pl-3 pr-1.5">
          {/* 胶囊磨砂底：首页随下滑由滚动驱动淡入(.zx-hd-solid)；其余页/降级时常显(实体) */}
          <div
            aria-hidden
            className="zx-hd-solid liquid-glass liquid-glass-float pointer-events-none absolute inset-0 rounded-full border border-line"
          />
          <Link to="/" className="relative z-10 flex items-center">
            <img
              src={logoUrl}
              alt="知行数据分析"
              className="header-logo zx-hd-logo h-8 w-auto"
            />
          </Link>
          <div className="relative z-10 flex items-center gap-0.5">
            {isAuthenticated && (
              <button
                type="button"
                onClick={() => navigate('/scan-login')}
                className={mobileIconBtnClass}
                aria-label="扫码登录电脑"
              >
                <ScanLine className="h-5 w-5" />
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate('/search')}
              className={mobileIconBtnClass}
              aria-label="搜索数据集"
            >
              <Search className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              className={mobileIconBtnClass}
              aria-label="打开菜单"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      <MobileMenu open={menuOpen} onClose={closeMenu}>
        {/* 高级搜索：全宽行（带图标） */}
        <MobileMenuLink to="/search" onClick={closeMenu} icon={<Search className="h-4 w-4" />}>
          进入高级搜索
        </MobileMenuLink>

        {/* 数据浏览：3 图标磁贴（已去掉「全部数据集」入口） */}
        <p className="px-1 pb-1 pt-2 text-[11px] font-medium tracking-wide text-fg-subtle">数据浏览</p>
        <div className="grid grid-cols-3 gap-2">
          <MenuTile
            to="/datasets?cat=company&lv=category"
            onClick={closeMenu}
            icon={<Building2 className="h-6 w-6" />}
            label="上市公司"
          />
          <MenuTile
            to="/datasets?cat=province&lv=category"
            onClick={closeMenu}
            icon={<MapIcon className="h-6 w-6" />}
            label="省级数据"
          />
          <MenuTile
            to="/datasets?cat=city&lv=category"
            onClick={closeMenu}
            icon={<PinIcon className="h-6 w-6" />}
            label="地级市"
          />
        </div>

        {/* 账户：兑换码在最前；登录后 个人中心/扫码登录，未登录 登录/注册 */}
        <p className="px-1 pb-1 pt-3 text-[11px] font-medium tracking-wide text-fg-subtle">账户</p>
        <div className="grid grid-cols-3 gap-2">
          <MenuTile
            to="/redeem"
            onClick={closeMenu}
            icon={<Ticket className="h-6 w-6" />}
            label="兑换码"
          />
          {isAuthenticated && user ? (
            <>
              <MenuTile
                to="/profile"
                onClick={closeMenu}
                icon={<User className="h-6 w-6" />}
                label="个人中心"
              />
              <MenuTile
                to="/scan-login"
                onClick={closeMenu}
                icon={<ScanLine className="h-6 w-6" />}
                label="扫码登录电脑"
              />
            </>
          ) : (
            <>
              <MenuTile
                onClick={() => {
                  closeMenu();
                  openAuthModal({ mode: 'login' });
                }}
                icon={<User className="h-6 w-6" />}
                label="登录"
              />
              <MenuTile
                onClick={() => {
                  closeMenu();
                  openAuthModal({ mode: 'register' });
                }}
                icon={<User className="h-6 w-6" />}
                label="注册"
                tone="solid"
              />
            </>
          )}
        </div>

        {isAuthenticated && user && (
          <button
            onClick={handleLogout}
            className="mt-2 block w-full rounded-control border border-danger-fill bg-danger-tint px-3 py-2 text-center text-sm font-medium text-danger transition-colors hover:bg-danger-fill hover:text-white"
          >
            退出登录
          </button>
        )}

        <div className="mx-1 my-2 border-t border-line-subtle" />
        <ThemeToggleSegment />
      </MobileMenu>
    </header>
  );
}

function navBlockClass(isActive: boolean) {
  return `relative whitespace-nowrap rounded-full px-3 py-2 text-[13px] transition-colors ${
    isActive
      ? 'bg-navy-fill font-medium text-white'
      : 'text-fg-muted hover:bg-surface-muted hover:text-fg'
  }`;
}

/**
 * "全部数据集"：仅当在 /datasets 路径且 ?cat 不存在时才高亮。
 * NavLink 自带的 `end` 只看 pathname，会让 ?cat=company 时也亮。
 */
function AllDatasetsNavLink() {
  const location = useLocation();
  const isOnDatasets = location.pathname === '/datasets';
  const hasCat = !!new URLSearchParams(location.search).get('cat');
  const isActive = isOnDatasets && !hasCat;
  return (
    <NavLink to="/datasets" className={navBlockClass(isActive)}>
      全部数据集
    </NavLink>
  );
}

/**
 * 顶级分类的 NavLink：除了路径匹配，还要看 ?cat= 查询参数是否吻合，
 * 否则在 /datasets?cat=company 时 4 个 datasets 类的 NavLink 都会高亮。
 */
function CategoryNavLink({
  to,
  catCode,
  children,
}: {
  to: string;
  catCode: string;
  children: React.ReactNode;
}) {
  const location = useLocation();
  const isOnDatasets = location.pathname === '/datasets';
  const currentCat = new URLSearchParams(location.search).get('cat');
  const isActive = isOnDatasets && currentCat === catCode;
  return (
    <NavLink to={to} className={navBlockClass(isActive)}>
      {children}
    </NavLink>
  );
}

/** 省级数据图标：简化版图/折叠地图（currentColor 描边）。 */
function MapIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M9 4 3 6.2v13.6l6-2.2 6 2.2 6-2.2V6L15 8.2 9 6Z" />
      <path d="M9 4v13.8" />
      <path d="M15 8.2V22" />
    </svg>
  );
}

/** 地级市数据图标：定位标记。 */
function PinIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 21.5s6.5-5.8 6.5-11a6.5 6.5 0 1 0-13 0c0 5.2 6.5 11 6.5 11Z" />
      <circle cx="12" cy="10.5" r="2.4" />
    </svg>
  );
}
